import { Dialog, DialogContent, Box } from "@mui/material";
import isEqual from "lodash/isEqual";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Props, State } from "./interfaces";
import { FieldsContainer } from "./styles";
import ConfirmationChangesDialog from "../../../../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../../../../containers/DialogTitle/DialogTitle";
import { useAppSelector } from "../../../../../../../custom-hooks/redux/redux";
import { CompanyLegalEntityRef } from "../../../../../../../models/company-accounts/company-legal-entities";
import { LegalEntitySIRef } from "../../../../../../../models/legal-entity-si/legal-entity-si-ref";
import { ShareWithLegalEntitySIDTO } from "../../../../../../../models/share-with-legal-entity-si";
import AddEditButton from "../../../../../../../reusable-components/AddEditButton/AddEditButton";
import AutocompleteDropdown from "../../../../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { UsersApiService } from "../../../../../../../services/users/users-api.service";

export const AddEditSharedWithUser = ({
  effect = "add",
  asLink = false,
  linkText,
  item,
  defaultLegalEntity = null,
  handleNotifyParent,
  buttonVariant,
  legalEntitySI,
}: Props) => {
  const { t } = useTranslation();

  const { currentCompanyAccount } = useAppSelector((state) => state.currentUserReducer);

  const DEFAULT_ITEM: Partial<ShareWithLegalEntitySIDTO> = useMemo(() => {
    return {
      id: item?.id ?? uuidv4(),
      legalEntity: defaultLegalEntity ?? item?.legalEntity ?? null,
      companyAccount: item?.companyAccount ?? currentCompanyAccount ?? null,
      user: item?.user ?? null,
    };
  }, [defaultLegalEntity, item, currentCompanyAccount]);

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      sharedWithUser: item ?? (DEFAULT_ITEM as ShareWithLegalEntitySIDTO),
      isCCDOpen: false,
      areFieldsValidated: effect === "edit",
    };
  }, [effect, DEFAULT_ITEM, item]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleChangeField = useCallback(
    (field: keyof ShareWithLegalEntitySIDTO, value: ShareWithLegalEntitySIDTO[keyof ShareWithLegalEntitySIDTO]) => {
      setState((state) => ({
        ...state,
        sharedWithUser: {
          ...state.sharedWithUser,
          [field]: value,
        },
      }));
    },
    []
  );

  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);

  const resetAndCloseDialog = (): void => {
    if (!isEqual(state.sharedWithUser, INITIAL_STATE.sharedWithUser)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };

  const handleSave = useCallback(async () => {
    handleNotifyParent({
      ...state.sharedWithUser,
      legalEntitySI: { id: legalEntitySI.id } as LegalEntitySIRef,
    });

    handleDiscardChanges();
  }, [handleDiscardChanges, handleNotifyParent, state.sharedWithUser, legalEntitySI]);

  useEffect(() => {
    effect === "add" &&
      setState((state) => ({
        ...state,
        sharedWithUser: {
          ...state.sharedWithUser,
          legalEntity: defaultLegalEntity ? (defaultLegalEntity as CompanyLegalEntityRef) : null,
        },
      }));
  }, [effect, defaultLegalEntity, currentCompanyAccount]);

  const areFieldsValidated = useCallback((): boolean => {
    return !!state.sharedWithUser.legalEntity && !!state.sharedWithUser.user;
  }, [state.sharedWithUser]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.sharedWithUser, areFieldsValidated]);

  useEffect(() => {
    !!item &&
      setState((state) => ({
        ...state,
        sharedWithUser: {
          ...item,
          legalEntity: item?.legalEntity ?? defaultLegalEntity,
        },
      }));

    return () => {
      if (effect === "add") setState({ ...INITIAL_STATE });
    };
  }, [item, effect, INITIAL_STATE, defaultLegalEntity]);

  return (
    <>
      <AddEditButton
        title={t(`buttons.${effect}`)}
        effect={effect}
        asLink={asLink}
        linkText={linkText}
        onClick={() => {
          setState((state) => ({ ...state, isDialogOpen: true }));
        }}
        {...(buttonVariant === "text" && { buttonText: "Edit" })}
      />
      <Dialog
        open={state.isDialogOpen}
        onClose={resetAndCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          title={"Shared with users"}
          handleClose={resetAndCloseDialog}
        />
        <DialogContent>
          <FieldsContainer>
            <Box>
              <AutocompleteDropdown
                labelKey={t("common_words.user")}
                field={"user"}
                serviceApi={new UsersApiService()}
                handleChange={handleChangeField}
                selectedValue={state.sharedWithUser.user ?? null}
                required
              />
            </Box>
          </FieldsContainer>
        </DialogContent>
        <DialogActions
          handleSave={handleSave}
          disabledSaveButton={!areFieldsValidated()}
          handleCancel={resetAndCloseDialog}
        />
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};
